import { useMemo, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import {
  highlightColorCssVar,
  stackRailPositions,
  type NumberedHighlight,
} from '@/utils/fileHighlights';

export interface PdfPageMetric {
  page: number;
  top: number;
  height: number;
  width: number;
}

interface FileHighlightRailProps {
  highlights: NumberedHighlight[];
  selectedId: string | null;
  hoveredId: string | null;
  highlightsVisible: boolean;
  pages: PdfPageMetric[];
  scrollHeight: number;
  /**
   * Content-space ring tops for text, Word, and spreadsheets.
   * PDF leaves this unset and uses page rectangles instead.
   */
  anchors?: { id: string; y: number }[];
  mobile?: boolean;
  innerRef: Ref<HTMLDivElement>;
  onSelect: (id: string) => void;
  onHover: (id: string | null) => void;
}

export function FileHighlightRail({
  highlights,
  selectedId,
  hoveredId,
  highlightsVisible,
  pages,
  scrollHeight,
  anchors,
  mobile = false,
  innerRef,
  onSelect,
  onHover,
}: FileHighlightRailProps) {
  const { t } = useTranslation();
  const pageByNumber = useMemo(() => new Map(pages.map((page) => [page.page, page])), [pages]);
  const anchorY = useMemo(
    () => new Map((anchors ?? []).map((anchor) => [anchor.id, anchor.y])),
    [anchors],
  );

  const desired = highlights.flatMap((highlight) => {
    if (anchors) {
      const y = anchorY.get(highlight.id);
      if (y == null) return [];
      return [{ id: highlight.id, number: highlight.number, color: highlight.color, y }];
    }
    const page = pageByNumber.get(highlight.page);
    const rect = highlight.rects[0];
    if (!page || !rect) return [];
    return [
      {
        id: highlight.id,
        number: highlight.number,
        color: highlight.color,
        y: page.top + rect.y * page.height + (rect.h * page.height) / 2 - 12,
      },
    ];
  });
  const stacked = stackRailPositions(desired.map(({ id, y }) => ({ id, y })));
  const yById = new Map(stacked.map((row) => [row.id, row.y]));

  return (
    <div
      className={cn('fp-rail', mobile && 'hidden')}
      aria-label={t('documents.highlights.railLabel')}
    >
      <div ref={innerRef} className="fp-rail-inner" style={{ height: Math.max(scrollHeight, 1) }}>
        {highlightsVisible && !mobile
          ? highlights.map((highlight) => {
              const y = yById.get(highlight.id);
              if (y == null) return null;
              return (
                <button
                  key={highlight.id}
                  type="button"
                  className={cn(
                    'fp-rail-num',
                    selectedId === highlight.id && 'is-on',
                    hoveredId === highlight.id && selectedId !== highlight.id && 'is-hov',
                  )}
                  style={{
                    top: y,
                    ['--hc' as string]: highlightColorCssVar(highlight.color),
                  }}
                  aria-label={t('documents.highlights.ringLabel', { n: highlight.number })}
                  onMouseEnter={() => onHover(highlight.id)}
                  onMouseLeave={() => onHover(null)}
                  onClick={() => onSelect(highlight.id)}
                >
                  {highlight.number}
                </button>
              );
            })
          : null}
      </div>
    </div>
  );
}
